// Exact nutrients: USDA FoodData Central data on the client (backend/src/services/usdaService.ts).

export interface UsdaPortion { label: string; grams: number }

export interface UsdaFoodDetail {
  fdcId: number;
  description: string;
  /** Everyday name from FuelTron's USDA copy ("Salmon, Atlantic, cooked"). */
  name?: string;
  dataType: string;
  brand?: string;
  per100g: Record<string, number>;
  measured: string[];
  portions: UsdaPortion[];
}

export interface UsdaSearchHit {
  fdcId: number;
  description: string;
  name?: string;
  dataType: string;
  brand?: string;
  caloriesPer100g?: number;
  proteinPer100g?: number;
  serving?: string;
}

/** Nutrients for `grams` of a USDA food: only the fields USDA measured. */
export function scaleUsda(per100g: Record<string, number>, grams: number): Record<string, number> {
  const out: Record<string, number> = {};
  const r = grams / 100;
  for (const [k, v] of Object.entries(per100g)) {
    out[k] = k === 'calories' ? Math.round(v * r) : Math.round(v * r * 100) / 100;
  }
  return out;
}

/** Grams for a quantity + unit when the unit is a weight; null otherwise. */
export function gramsFromUnit(quantity: number, unit: string): number | null {
  const u = String(unit || '').toLowerCase().trim();
  if (!(quantity > 0)) return null;
  if (u === 'g' || u === 'gram' || u === 'grams') return quantity;
  if (u === 'oz' || u === 'ounce' || u === 'ounces') return quantity * 28.3495;
  if (u === 'lb' || u === 'lbs' || u === 'pound' || u === 'pounds') return quantity * 453.592;
  return null;
}

/**
 * A food with USDA numbers for `grams`. Fields USDA measured are replaced; the
 * rest come from the food's original estimate, scaled to the new weight when
 * the estimate had a weight. `_estimate` keeps that original (so a re-match or
 * "use the estimate" never mixes in an earlier match) and `_usda` the per-100 g
 * data for exact re-scaling. The server drops both on save.
 */
export function applyUsda(food: Record<string, any>, usda: Pick<UsdaFoodDetail, 'fdcId' | 'description' | 'per100g' | 'measured'> & { name?: string }, grams: number, display?: { quantity?: number; unit?: string; name?: string }): Record<string, any> {
  const estimate: Record<string, any> = food._estimate || (() => { const { _usda, _estimate, ...orig } = food; void _usda; void _estimate; return orig; })();
  const baseGrams = Number(estimate.grams) > 0 ? Number(estimate.grams) : null;
  const ratio = baseGrams ? grams / baseGrams : 1;
  const out: Record<string, any> = { ...food, _estimate: estimate };
  const measured = new Set(usda.measured);
  for (const [k, v] of Object.entries(estimate)) {
    if (typeof v !== 'number' || measured.has(k) || ['quantity', 'confidence', 'grams', 'fdcId'].includes(k)) continue;
    out[k] = Math.round(v * ratio * 100) / 100;
  }
  Object.assign(out, scaleUsda(usda.per100g, grams));
  out.grams = Math.round(grams * 10) / 10;
  out.nutritionSource = 'usda';
  out.fdcId = usda.fdcId;
  out.usdaName = usda.name || usda.description;
  out._usda = { per100g: usda.per100g, measured: usda.measured, description: usda.description, fdcId: usda.fdcId };
  if (display?.quantity != null) out.quantity = display.quantity;
  if (display?.unit) out.unit = display.unit;
  if (display?.name) out.name = display.name;
  return out;
}

/** Numbers on a food that are not amounts of a nutrient, so a new weight never scales them. */
const NOT_AMOUNTS = new Set(['quantity', 'confidence', 'grams', 'photoGrams', 'fdcId', 'id', '_id', 'index', 'order']);

/**
 * The same food at a new weight (the photo result's one-tap portion buttons).
 * USDA-matched foods re-scale exactly from their per-100 g data; estimates scale every
 * nutrient in proportion. A weight-unit quantity follows the grams; any other unit
 * ("1.5 cup") scales by the same ratio. Returns null for 0 g ("None" for an added oil).
 */
export function reweighFood(food: Record<string, any>, grams: number): Record<string, any> | null {
  if (!(grams > 0)) return null;
  const current = Number(food.grams) > 0 ? Number(food.grams) : gramsFromUnit(Number(food.quantity), food.unit);
  if (!current || !(current > 0)) return { ...food, grams };
  const ratio = grams / current;
  const u = String(food.unit || '').toLowerCase();
  const quantity = u === 'g' || u === 'grams' ? Math.round(grams)
    : u === 'oz' || u === 'ounces' ? Math.round((grams / 28.3495) * 10) / 10
    : Math.round(Number(food.quantity || 1) * ratio * 100) / 100;
  if (food._usda) return { ...applyUsda(food, food._usda, grams), quantity };
  const out: Record<string, any> = { ...food, grams: Math.round(grams * 10) / 10, quantity };
  for (const [k, v] of Object.entries(food)) {
    if (typeof v !== 'number' || !Number.isFinite(v) || NOT_AMOUNTS.has(k)) continue;
    out[k] = k === 'calories' ? Math.round(v * ratio) : Math.round(v * ratio * 100) / 100;
  }
  return out;
}

/** "1 small breast" → "small breast", for showing "2 small breast". */
export const portionUnit = (label: string) => label.replace(/^1\s+/, '');

/** The name to show for a USDA hit: brand first for packaged foods. */
export const usdaDisplayName = (h: { description: string; name?: string; brand?: string; dataType?: string }) =>
  h.name ? h.name : h.dataType === 'Branded' && h.brand && !h.description.toLowerCase().includes(h.brand.toLowerCase())
    ? `${h.brand} ${h.description}` : h.description;

/** A food without the client-only USDA working data, for sending to the server. */
export function forServer(food: Record<string, any>): Record<string, any> {
  const { _usda, _estimate, usdaName, ...rest } = food;
  void _usda; void _estimate; void usdaName;
  return rest;
}
